import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AppNotification } from '../../models/notification.interface';
import { TimeAgoPipe } from '../../pipes/time-ago.pipe';

@Component({
  selector: 'app-notifications-panel',
  standalone: true,
  imports: [CommonModule, TimeAgoPipe],
  templateUrl: './notifications-panel.component.html',
  styleUrls: ['./notifications-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsPanelComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() notifications: AppNotification[] | null = [];
  @Input() hasError: boolean | null = false;

  @Output() notificationClick = new EventEmitter<AppNotification>();
  @Output() viewPreviousClick = new EventEmitter<void>();
  @Output() markAllAsReadClick = new EventEmitter<void>();

  @ViewChild('list', { static: false })
  listRef?: ElementRef<HTMLUListElement>;

  hasMore = false;

  private resizeObserver?: ResizeObserver;
  private rafHandle?: number;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly zone: NgZone,
  ) {}

  get hasUnread(): boolean {
    return !!this.notifications?.some((n) => !n.isRead);
  }

  get hasContent(): boolean {
    return !this.hasError && (this.notifications?.length ?? 0) > 0;
  }

  get isEmpty(): boolean {
    return !this.hasError && (this.notifications?.length ?? 0) === 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notifications']) {
      this.scheduleMeasure();
    }
  }

  ngAfterViewInit(): void {
    const list = this.listRef?.nativeElement;
    if (!list) {
      return;
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.zone.runOutsideAngular(() => {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver?.observe(list);
      });
    }

    this.scheduleMeasure();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.rafHandle !== undefined) {
      cancelAnimationFrame(this.rafHandle);
    }
  }

  trackById(_: number, notification: AppNotification): number {
    return notification.id;
  }

  onNotificationClick(notification: AppNotification): void {
    this.notificationClick.emit(notification);
  }

  onViewPreviousClick(): void {
    this.viewPreviousClick.emit();
  }

  onMarkAllAsReadClick(): void {
    this.markAllAsReadClick.emit();
  }

  private scheduleMeasure(): void {
    if (this.rafHandle !== undefined) {
      return;
    }
    this.rafHandle = requestAnimationFrame(() => {
      this.rafHandle = undefined;
      this.measure();
    });
  }

  private measure(): void {
    const list = this.listRef?.nativeElement;
    if (!list) {
      return;
    }

    const items = Array.from(list.children) as HTMLElement[];

    for (const item of items) {
      item.classList.remove('is-out');
    }
    // Force a reflow so measurements below reflect the reset state.
    void list.offsetHeight;

    const listHeight = list.clientHeight;
    const listTop = list.getBoundingClientRect().top;

    let firstHiddenIndex = -1;
    for (let i = 0; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      const relativeTop = rect.top - listTop;
      const relativeBottom = relativeTop + items[i].offsetHeight;
      if (relativeBottom > listHeight + 0.5) {
        firstHiddenIndex = i;
        break;
      }
    }

    if (firstHiddenIndex !== -1) {
      for (let i = firstHiddenIndex; i < items.length; i++) {
        items[i].classList.add('is-out');
      }
    }

    const nextHasMore = firstHiddenIndex !== -1;
    if (nextHasMore !== this.hasMore) {
      this.zone.run(() => {
        this.hasMore = nextHasMore;
        this.cdr.detectChanges();
      });
    }
  }
}
